import { ReactNode } from 'react';

import { DemandInformationVessel, VesselState } from '../../../types/DemandInformationMessage';
import { CategoryState } from '../../../types/ReachInformationMessage';
import type { ComboBoxOption } from '../../cdl/ComboBox/ComboBox';
import { DynamicFilterContainer, Filter } from '../../cdl/Filter/DynamicFilterContainer/DynamicFilterContainer';
import { FilterMultiSelectFull } from '../../cdl/Filter/FilterMultiSelectFull/FilterMultiSelectFull';
import {
    FilterMultiSelectOption,
    FilterMultiSelectShort,
} from '../../cdl/Filter/FilterMultiSelectShort/FilterMultiSelectShort';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { useCustomerList } from '../../group/customer/detail/hooks/useCustomerList';
import { getHighestPriorityCategoryState } from '../utils/getHighestPriorityCategoryState';

interface FilterContainerProps {
    allEntries: DemandInformationVessel[];
    children: (filteredItems: DemandInformationVessel[]) => ReactNode;
    groupId?: string;
    headerAction?: ReactNode;
}

export const VesselDemandFilter = ({ allEntries, children, groupId, headerAction }: FilterContainerProps) => {
    const role = useRole();
    const customerQuery = useCustomerList({
        groupId: role.isSuperAdmin() ? groupId : role.group.id,
    });

    const stateFilter: Filter<VesselState, DemandInformationVessel> = {
        filterFn: (value, entry) => value.includes(entry.state),
        component: (values, setValues) => {
            const availableVesselStates = Array.from(new Set(allEntries.map((vessel) => vessel.state)));

            const optionsWithLabel: FilterMultiSelectOption<VesselState>[] = availableVesselStates.map((value) => {
                return {
                    label: translate(`demand.filter.vesselState.${value as VesselState}`),
                    value: value as VesselState,
                };
            });

            return (
                <FilterMultiSelectShort<VesselState>
                    label={translate('demand.filter.vesselState.state')}
                    filterByLabel={translate('demand.filter.vesselState.filterByState')}
                    values={values}
                    options={optionsWithLabel}
                    onChange={setValues}
                />
            );
        },
    };

    const vesselFilter: Filter<string, DemandInformationVessel> = {
        filterFn: (value, entry) => value.includes(entry.id),
        component: (values, setValues) => {
            const optionsWithLabel: ComboBoxOption[] = allEntries.map((value) => {
                return {
                    label: value.name,
                    value: value.id,
                };
            });

            return (
                <FilterMultiSelectFull
                    label={translate('demand.filter.vessel.vessel')}
                    filterByLabel={translate('demand.filter.vessel.filterByVessel')}
                    filterPlaceholder={translate('demand.filter.vessel.searchForVessel')}
                    options={optionsWithLabel}
                    values={values}
                    onChange={setValues}
                />
            );
        },
    };

    const companyFilter: Filter<string, DemandInformationVessel> = {
        filterFn: (value, entry) => value.includes(entry.customerId),
        component: (values, setValues) => {
            const availableCompanies = customerQuery.data ?? [];

            const optionsWithLabel: FilterMultiSelectOption<string>[] = availableCompanies.map((value) => {
                return {
                    label: formatCompanyName({ company: value })!,
                    value: value.id,
                };
            });

            return (
                <FilterMultiSelectShort
                    label={translate('demand.filter.company.company')}
                    filterByLabel={translate('demand.filter.company.filterByCompany')}
                    values={values}
                    options={optionsWithLabel}
                    onChange={setValues}
                />
            );
        },
    };

    const reachStatusFilter: Filter<CategoryState, DemandInformationVessel> = {
        filterFn: (value, entry) => {
            const categoryStateToShow = getHighestPriorityCategoryState(entry.categories);
            return categoryStateToShow !== null && value.includes(categoryStateToShow);
        },
        component: (values, setValues) => {
            const availableCategoryStates = Array.from(
                new Set(
                    allEntries
                        .map((vessel) => getHighestPriorityCategoryState(vessel.categories))
                        .filter((state): state is CategoryState => state !== null)
                )
            );

            const optionsWithLabel: FilterMultiSelectOption<CategoryState>[] = availableCategoryStates.map((value) => {
                let label: string;
                switch (value) {
                    case CategoryState.WARNING_LIMIT:
                        label = translate('demand.warningLimit');
                        break;
                    case CategoryState.SAFETY_RESERVE:
                        label = translate('demand.onSafetyReserve');
                        break;
                    case CategoryState.OUT_OF_REACH:
                        label = translate('demand.outOfReach');
                        break;
                    case CategoryState.IN_REACH:
                        label = translate('demand.inReach');
                        break;
                }

                return {
                    label: label,
                    value: value,
                };
            });

            const sortOptions = (
                a: FilterMultiSelectOption<CategoryState>,
                b: FilterMultiSelectOption<CategoryState>
            ) => {
                const sortOrder = [
                    CategoryState.IN_REACH,
                    CategoryState.WARNING_LIMIT,
                    CategoryState.SAFETY_RESERVE,
                    CategoryState.OUT_OF_REACH,
                ];

                const indexA = sortOrder.indexOf(a.value);
                const indexB = sortOrder.indexOf(b.value);

                return indexA - indexB;
            };

            return (
                <FilterMultiSelectShort<CategoryState>
                    label={translate('demand.filter.reachStatus.reachStatus')}
                    filterByLabel={translate('demand.filter.reachStatus.filterByReachStatus')}
                    values={values}
                    options={optionsWithLabel}
                    onChange={setValues}
                    sortOptions={sortOptions}
                />
            );
        },
    };

    const filters: Record<string, Filter<any, DemandInformationVessel>> = role.isOneCompanyUser() &&
    !role.isSuperAdmin()
        ? {
              vessel: vesselFilter,
              state: stateFilter,
              reachStatus: reachStatusFilter,
          }
        : {
              company: companyFilter,
              vessel: vesselFilter,
              state: stateFilter,
              reachStatus: reachStatusFilter,
          };

    return (
        <DynamicFilterContainer allEntries={allEntries} filters={filters} headerAction={headerAction}>
            {children}
        </DynamicFilterContainer>
    );
};
